import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);
const PROFILE_CACHE_KEY = 'getscribedash.profile';

function readCachedProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY)) || null; }
  catch { return null; }
}

function cacheProfile(profile) {
  if (profile) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  else localStorage.removeItem(PROFILE_CACHE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedProfile());
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      cacheProfile(data);
    } catch (error) {
      // Only a confirmed invalid session clears authentication. Network,
      // timeout, and background-service failures retain the hydrated profile.
      if (error.response?.status === 401) {
        setUser(false);
        cacheProfile(null);
      } else {
        setUser((current) => current || false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    const expire = () => { setUser(false); cacheProfile(null); setLoading(false); };
    window.addEventListener('auth:session-expired', expire);
    return () => window.removeEventListener('auth:session-expired', expire);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    cacheProfile(data);
    return data;
  };

  const register = async (email, password, name, roiProfile = {}) => {
    const referral = new URLSearchParams(window.location.search).get('ref');
    const { data } = await api.post('/auth/register', { email, password, name, ...roiProfile }, {
      params: referral ? { ref: referral } : undefined,
    });
    setUser(data);
    cacheProfile(data);
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
    cacheProfile(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
